import { useMemo, useState } from "react";
import { Box } from "lucide-react";
import { cn } from "@/lib/utils";
import { getProjectSubsystems3D } from "@/lib/projectSubsystems3d";

export type ArtifactDespiecePanelProps = {
  projectTitle: string;
  className?: string;
};

export function ArtifactDespiecePanel({ projectTitle, className }: ArtifactDespiecePanelProps) {
  const subsystems = useMemo(() => getProjectSubsystems3D(projectTitle), [projectTitle]);
  const maxStep = Math.max(0, subsystems.length - 1);

  const [explodeFactor, setExplodeFactor] = useState(35);
  const [assemblyStepIdx, setAssemblyStepIdx] = useState(Math.min(4, maxStep));
  const [selectedPartIdx, setSelectedPartIdx] = useState(0);

  const part = subsystems[selectedPartIdx] ?? subsystems[0];

  return (
    <div className={cn("space-y-3 font-mono-tech", className)}>
      <div className="flex items-start gap-2">
        <Box className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-sky-400 font-bold">
            Despiece & Montaje 3D
          </p>
          <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
            Explosión isométrica e inspector de pieza.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-sky-500/40 bg-[#071018] p-3 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-[10px]">
          <span className="text-sky-400 font-bold">
            ISO · {explodeFactor}% · Etapa {assemblyStepIdx + 1}/{subsystems.length || 5}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setExplodeFactor(0)}
              className="px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/40 text-[10px] cursor-pointer"
            >
              Ensamblar 0%
            </button>
            <button
              type="button"
              onClick={() => setExplodeFactor(85)}
              className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] cursor-pointer"
            >
              Despiece 85%
            </button>
          </div>
        </div>

        <div className="relative h-44 w-full flex items-center justify-center rounded-lg border border-sky-500/20 bg-[#050b11] overflow-hidden">
          <svg viewBox="0 0 500 300" className="w-full h-full">
            <g stroke="rgba(56,189,248,0.1)" strokeWidth="1">
              <line x1="50" y1="250" x2="450" y2="250" />
              <line x1="100" y1="280" x2="400" y2="120" />
              <line x1="400" y1="280" x2="100" y2="120" />
              <line
                x1="250"
                y1="20"
                x2="250"
                y2="285"
                strokeDasharray="4 4"
                stroke="rgba(56,189,248,0.35)"
              />
            </g>

            {subsystems.map((sub, idx) => {
              const isMounted = idx <= assemblyStepIdx;
              const isSelected = idx === selectedPartIdx;
              const baseY = 215 - idx * 32;
              const offset = (idx - 2) * (explodeFactor * 0.42);
              const yPos = baseY - offset;

              if (!isMounted) return null;

              return (
                <g
                  key={sub.step}
                  onClick={() => setSelectedPartIdx(idx)}
                  className="cursor-pointer"
                >
                  <polygon
                    points={`250,${yPos - 24} 345,${yPos} 250,${yPos + 24} 155,${yPos}`}
                    fill={isSelected ? `${sub.color}44` : `${sub.color}22`}
                    stroke={sub.color}
                    strokeWidth={isSelected ? "3" : "1.8"}
                  />
                  <polygon
                    points={`155,${yPos} 250,${yPos + 24} 250,${yPos + 40} 155,${yPos + 16}`}
                    fill={`${sub.color}33`}
                    stroke={sub.color}
                    strokeWidth="1.5"
                  />
                  <polygon
                    points={`345,${yPos} 250,${yPos + 24} 250,${yPos + 40} 345,${yPos + 16}`}
                    fill={`${sub.color}18`}
                    stroke={sub.color}
                    strokeWidth="1.5"
                  />
                  <line
                    x1="345"
                    y1={yPos + 6}
                    x2="375"
                    y2={yPos + 6}
                    stroke={sub.color}
                    strokeWidth="1.5"
                  />
                  <text
                    x="380"
                    y={yPos + 10}
                    fill={isSelected ? "#ffffff" : sub.color}
                    fontSize="11"
                    fontWeight={isSelected ? "bold" : "normal"}
                    fontFamily="monospace"
                  >
                    #{sub.step} {sub.name.slice(3, 22)}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        <div className="grid grid-cols-1 gap-3 pt-2 border-t border-sky-500/20 text-[10px]">
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-muted-foreground">Despiece</span>
              <strong className="text-sky-400">{explodeFactor}%</strong>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={explodeFactor}
              onChange={(e) => setExplodeFactor(Number(e.target.value))}
              className="w-full accent-sky-500 cursor-pointer"
            />
          </div>
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-muted-foreground">Montaje</span>
              <strong className="text-emerald-400">
                Etapa {assemblyStepIdx + 1} / {subsystems.length || 5}
              </strong>
            </div>
            <input
              type="range"
              min={0}
              max={maxStep}
              value={Math.min(assemblyStepIdx, maxStep)}
              onChange={(e) => {
                const val = Number(e.target.value);
                setAssemblyStepIdx(val);
                setSelectedPartIdx(val);
              }}
              className="w-full accent-emerald-500 cursor-pointer"
            />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card/60 p-3 space-y-2.5">
        <div className="flex items-center justify-between border-b border-border pb-1.5">
          <span className="text-[9px] font-bold text-primary uppercase tracking-wider">
            Inspector
          </span>
          <span className="text-[10px] font-bold text-sky-400">
            Pieza {selectedPartIdx + 1} / {subsystems.length}
          </span>
        </div>

        <div className="flex flex-wrap gap-1">
          {subsystems.map((s, idx) => (
            <button
              key={s.step}
              type="button"
              onClick={() => {
                setSelectedPartIdx(idx);
                if (assemblyStepIdx < idx) setAssemblyStepIdx(idx);
              }}
              className={cn(
                "px-2 py-0.5 rounded-md text-[10px] font-bold border cursor-pointer transition-colors",
                selectedPartIdx === idx
                  ? "bg-sky-500/20 text-sky-300 border-sky-500/50"
                  : "bg-background border-border text-muted-foreground hover:text-foreground"
              )}
            >
              #{s.step}
            </button>
          ))}
        </div>

        {part ? (
          <div className="rounded-lg bg-background border border-border p-2.5 space-y-2 text-[11px]">
            <h5 className="font-bold text-[12px] leading-snug" style={{ color: part.color }}>
              {part.name}
            </h5>
            <p className="text-muted-foreground leading-relaxed font-sans">{part.role}</p>
            <div className="space-y-1.5 pt-1.5 border-t border-border/60">
              <div>
                <span className="text-muted-foreground">Cotas: </span>
                <strong className="text-foreground">{part.dims}</strong>
              </div>
              <div>
                <span className="text-muted-foreground">Material: </span>
                <strong className="text-emerald-400">{part.material}</strong>
              </div>
              <div>
                <span className="text-muted-foreground">Fijación: </span>
                <strong className="text-amber-400">{part.fastener}</strong>
              </div>
              <div className="rounded-md bg-sky-500/10 border border-sky-500/30 p-2 mt-1">
                <span className="text-sky-400 font-bold block text-[10px] mb-0.5">Física</span>
                <span className="text-foreground font-sans text-[11px] leading-relaxed">
                  {part.physics}
                </span>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
